import SETTING_KEYS from '../../../constants/setting-keys';
import type { RegionalInfo, ISource } from '../../../store/implant-context';
import { formatMRIParameters } from '../../reports/cpt/utils/cptDocumentViewUtils';
import { getMriStatusText } from '../../reports/cpt/utils/cptFormUtils';
import { resolveIfuVersion } from './implantPreviewData';
import { formatConditionalsListText } from '../../preview/conditionalsLists';
import { wrapLine } from '../../documents/utils/documentViewUtils';
import { resolveLastCheckedDate } from '../implantDateUtils';

const LINE_WIDTH = 52;
const SEPARATOR = '————————————————————————————————';
const INDENT = '  ';

function richTextToPlain(html: string): string {
    if (!html) return '';
    let text = html;

    text = text.replace(/<hr\s*\/?>/gi, '\n---\n');
    text = text.replace(/<h3[^>]*>(.*?)<\/h3>/gi, '\n$1\n');
    text = text.replace(/<h4[^>]*>(.*?)<\/h4>/gi, '\n$1\n');
    text = text.replace(/<strong>(.*?)<\/strong>/gi, '**$1**');
    text = text.replace(/<b>(.*?)<\/b>/gi, '**$1**');
    text = text.replace(/<em>(.*?)<\/em>/gi, '_$1_');
    text = text.replace(/<i>(.*?)<\/i>/gi, '_$1_');
    text = text.replace(/<u>(.*?)<\/u>/gi, '$1');
    text = text.replace(/<ul[^>]*>/gi, '\n');
    text = text.replace(/<\/ul>/gi, '');
    text = text.replace(/<ol[^>]*>/gi, '\n');
    text = text.replace(/<\/ol>/gi, '');
    text = text.replace(/<li[^>]*>/gi, '• ');
    text = text.replace(/<\/li>/gi, '\n');
    text = text.replace(/<br\s*\/?>/gi, '\n');
    text = text.replace(/<\/p>/gi, '\n');
    text = text.replace(/<\/(div|tr)>/gi, '\n');
    text = text.replace(/<(p|div)[^>]*>/gi, '');
    text = text.replace(/<[^>]+>/g, '');

    text = text
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&apos;/g, "'");

    text = text.replace(/[ \t]+$/gm, '');
    text = text.replace(/^[ \t]+$/gm, '');
    text = text.replace(/\n{3,}/g, '\n\n');

    text = text
        .split('\n')
        .map((line) => wrapLine(line, LINE_WIDTH))
        .join('\n');

    return text.trim();
}

function indentBlock(text: string): string {
    return text
        .split('\n')
        .map((line) => wrapLine(line, LINE_WIDTH - INDENT.length))
        .join('\n')
        .split('\n')
        .map((line) => `${INDENT}${line}`)
        .join('\n');
}

type TFunc = (key: string, opts?: Record<string, any>) => string;

export interface ImplantPlainTextInput {
    info: any;
    regionalInfo: RegionalInfo;
    conditionals: any;
    approvalInfo?: any;
    departmentNotes?: any[];
}

export function buildImplantPlainText(input: ImplantPlainTextInput, t: TFunc): string {
    const { info, regionalInfo, conditionals, approvalInfo, departmentNotes } =
        input;
    const parts: string[] = [];

    const model = regionalInfo.model || info[SETTING_KEYS.model] || '';
    const manufacturer = info[SETTING_KEYS.manufacturer] || '';
    const type = info[SETTING_KEYS.type] || '';
    const mriStatus = getMriStatusText(regionalInfo.mriStatus);
    const mriCode = info[SETTING_KEYS.mriCode] || '';
    const ifuVersion = resolveIfuVersion(info, regionalInfo);

    const headerLines: string[] = [];
    headerLines.push(
        `${model.toUpperCase()}${mriStatus ? `  [${mriStatus}]` : ''}`
    );
    if (manufacturer) {
        const desc = type ? `${manufacturer} · ${type}` : manufacturer;
        headerLines.push(desc);
    }
    headerLines.push(SEPARATOR);
    if (mriCode) headerLines.push(`${t('implant.preview.mriCode')}: ${mriCode}`);
    if (ifuVersion) headerLines.push(`${t('implant.preview.ifuVersion')}: ${ifuVersion}`);
    const lastCheckedDate = resolveLastCheckedDate(info, regionalInfo);
    if (lastCheckedDate) {
        headerLines.push(
            `${t('implant.preview.lastChecked')}: ${new Date(lastCheckedDate as any).toLocaleDateString()}`
        );
    }
    parts.push(headerLines.join('\n'));

    if (approvalInfo?.status === 'Approved') {
        const approvalLines: string[] = ['', t('implant.preview.localApproval').toUpperCase(), SEPARATOR];
        const facilityName =
            approvalInfo.facilityName || approvalInfo.facility;
        if (facilityName) {
            approvalLines.push(t('implant.preview.approvedAt', { facility: facilityName }));
        }
        if (approvalInfo.approvedBy) {
            approvalLines.push(`${t('implant.preview.approver')}: ${approvalInfo.approvedBy}`);
        }
        if (approvalInfo.approvedAt) {
            approvalLines.push(
                `${t('preview.common.date')}: ${new Date(approvalInfo.approvedAt).toLocaleDateString()}`
            );
        }
        if (approvalInfo.comment) {
            approvalLines.push(`${t('implant.preview.comment')}: ${approvalInfo.comment}`);
        }
        parts.push(approvalLines.join('\n'));
    }

    const notesWithContent = (departmentNotes ?? []).filter(
        (note: any) => note?.title || richTextToPlain(note?.note || '')
    );
    if (notesWithContent.length > 0) {
        const noteLines: string[] = ['', t('implant.preview.departmentNotes').toUpperCase(), SEPARATOR];
        for (const note of notesWithContent) {
            const addedBy = note.editLog?.[0]?.name;
            noteLines.push('');
            noteLines.push(
                `${note.title || 'Note'}${addedBy ? `  (${addedBy})` : ''}`
            );
            const text = richTextToPlain(note.note || '');
            if (text) {
                noteLines.push(indentBlock(text));
            }
        }
        parts.push(noteLines.join('\n'));
    }

    if (conditionals && regionalInfo.mriStatus === 2) {
        const condLines: string[] = ['', t('implant.preview.mriConditions').toUpperCase(), SEPARATOR];

        if (regionalInfo.precaution && typeof regionalInfo.precaution === 'string') {
            const precautionText = richTextToPlain(regionalInfo.precaution);
            if (precautionText) {
                condLines.push('');
                condLines.push(t('implant.preview.precaution'));
                condLines.push(indentBlock(precautionText));
            }
        }

        const formatted = formatMRIParameters(conditionals);
        for (const [label, rawValue] of Object.entries(formatted)) {
            if (label === 'Precautions') continue;

            const listText = formatConditionalsListText(label, conditionals, t);
            if (listText) {
                condLines.push('');
                condLines.push(label);
                condLines.push(indentBlock(listText));
                continue;
            }

            if (!rawValue || (Array.isArray(rawValue) && rawValue.length === 0)) continue;
            const value = typeof rawValue === 'string' ? rawValue : String(rawValue);
            if (value === '<p></p>') continue;

            const text = richTextToPlain(value);
            if (!text) continue;

            condLines.push('');
            condLines.push(label);
            condLines.push(indentBlock(text));
        }

        if (
            regionalInfo.customFields?.some((f: any) => f.isCritical && f.value)
        ) {
            for (const field of regionalInfo.customFields.filter(
                (f: any) => f.isCritical && f.value
            )) {
                condLines.push('');
                condLines.push(field.heading);
                condLines.push(indentBlock(richTextToPlain(String(field.value))));
            }
        }

        parts.push(condLines.join('\n'));
    }

    const nonCriticalCustomFields = (regionalInfo.customFields ?? []).filter(
        (f: any) => !f.isCritical && f.heading && f.value
    );
    if (nonCriticalCustomFields.length > 0) {
        const customLines: string[] = ['', t('implant.preview.additionalInfo').toUpperCase(), SEPARATOR];
        for (const field of nonCriticalCustomFields) {
            const raw =
                typeof field.value === 'string'
                    ? field.value
                    : String(field.value);
            const val = richTextToPlain(raw);
            customLines.push('');
            customLines.push(field.heading);
            customLines.push(indentBlock(val));
        }
        parts.push(customLines.join('\n'));
    }

    const notesText = richTextToPlain(info[SETTING_KEYS.notes] ?? '');
    if (notesText) {
        parts.push(`\n${t('implant.preview.notes').toUpperCase()}\n${SEPARATOR}\n${notesText}`);
    }

    const sources: ISource[] = regionalInfo.sources ?? [];
    if (sources.length > 0) {
        const sourceLines: string[] = ['', t('implant.preview.sources').toUpperCase(), SEPARATOR];
        for (const source of sources) {
            const datePart = source.documentDate
                ? ` (${new Date(source.documentDate.toString()).toLocaleDateString()})`
                : '';
            sourceLines.push(
                `${source.title || t('implant.preview.sources')}${datePart}`
            );
            if (source.content) {
                sourceLines.push(indentBlock(source.content));
            }
        }
        parts.push(sourceLines.join('\n'));
    }

    return parts.join('\n');
}
